import { useCallback, useState } from 'react';
import { useGlobalToast } from '@repo/atomic-components';
import { isEndpointLockError, usePostGdprStartReviewMutation } from '@/api';

/**
 * Starts a document's GDPR review, if it hasn't been started yet, before its
 * check page is opened. `startGdprCheck` resolves `true` when the check page
 * can be opened.
 *
 * Start-review can only be called once per document, and the row may be stale
 * (e.g. the review was started from another tab), so the latest `checkStarted`
 * is fetched before calling it. If the document is missing from the refreshed
 * list (filtered out, moved to another page or deleted) its status is unknown,
 * so it's treated as a failure rather than "not started". The document's button
 * should be disabled while its id is in `startingGdprCheckDocumentIds`. A
 * failure shows an error toast and resolves `false`; a refusal for an
 * endpoint lock skips the toast, as the lock has already shown its own.
 */
export const useStartGdprCheck = () => {
  const [triggerPostGdprStartReview] = usePostGdprStartReviewMutation();
  const globalToastRef = useGlobalToast();
  const [startingGdprCheckDocumentIds, setStartingGdprCheckDocumentIds] = useState<string[]>([]);

  const startGdprCheck = useCallback(
    async (
      documentId: string,
      checkStarted: boolean,
      fetchLatestDocument: () => Promise<{ checkStarted: string | null } | undefined>,
    ) => {
      if (checkStarted) return true;

      setStartingGdprCheckDocumentIds((ids) => [...ids, documentId]);
      try {
        const latestDocument = await fetchLatestDocument();
        if (!latestDocument) {
          throw new Error(`Document ${documentId} is missing from the refreshed list`);
        }
        if (!latestDocument.checkStarted) {
          await triggerPostGdprStartReview({ documentId }).unwrap();
        }
        return true;
      } catch (error) {
        console.error('Error starting GDPR review:', error);
        if (isEndpointLockError(error)) return false;
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'GDPR check failed to start',
          detail: 'Please try again.',
          closable: true,
          life: 5000,
        });
        return false;
      } finally {
        setStartingGdprCheckDocumentIds((ids) => ids.filter((id) => id !== documentId));
      }
    },
    [triggerPostGdprStartReview, globalToastRef],
  );

  return { startGdprCheck, startingGdprCheckDocumentIds };
};
